import React, { useState, useEffect } from 'react';
import {
  X,
  Upload,
  AlertCircle,
  CheckCircle2,
  FileText,
  DollarSign,
  Calendar,
  Smartphone,
  Building,
  HelpCircle,
  Clock,
  ShieldAlert,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { sendEmail } from '../../lib/emailService';
import type { Invoice, Payment } from '../../types/database';

interface PaymentInstruction {
  id: string;
  channel_type: string;
  provider: string;
  account_number: string;
  account_holder: string;
  branch: string;
  currency: string;
  instructions: string;
  is_active: boolean;
}

export interface CoursePaymentInfo {
  id: string;
  title: string;
  price: number;
  currency?: string;
}

interface ManualReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice?: Invoice | null;
  course?: CoursePaymentInfo | null;
  existingPayment?: Payment | null;
  onSuccess: () => void;
}

export default function ManualReceiptModal({
  isOpen,
  onClose,
  invoice,
  course,
  existingPayment,
  onSuccess,
}: ManualReceiptModalProps) {
  const { user, profile } = useAuth();

  const [instructions, setInstructions] = useState<PaymentInstruction[]>([]);
  const [selectedInstructionId, setSelectedInstructionId] = useState<string>('');

  const expectedAmount = Number(
    course
      ? course.price
      : (invoice?.balance_due ??
        (invoice ? invoice.total_amount - (invoice.amount_paid || 0) : existingPayment?.amount || 0))
  );

  const [submittedAmount, setSubmittedAmount] = useState<string>(
    expectedAmount > 0 ? expectedAmount.toString() : ''
  );
  const [currency, setCurrency] = useState<string>(course?.currency || invoice?.currency || 'USD');
  const [paymentMethod, setPaymentMethod] = useState<string>('MTN Mobile Money');
  const [transactionRef, setTransactionRef] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [customerNotes, setCustomerNotes] = useState<string>('');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPaymentInstructions();
      if (existingPayment) {
        setSubmittedAmount(existingPayment.amount?.toString() || '');
        setTransactionRef(existingPayment.payment_reference || '');
      }
    }
  }, [isOpen, existingPayment]);

  const fetchPaymentInstructions = async () => {
    try {
      const { data } = await supabase
        .from('payment_instructions')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (data && data.length > 0) {
        setInstructions(data as PaymentInstruction[]);
        setSelectedInstructionId(data[0].id);
        setPaymentMethod(data[0].provider);
      }
    } catch (err) {
      console.error('Error fetching payment instructions:', err);
    }
  };

  const handleInstructionChange = (id: string) => {
    setSelectedInstructionId(id);
    const inst = instructions.find((i) => i.id === id);
    if (inst) setPaymentMethod(inst.provider);
  };

  const handleSubmitReceipt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!receiptFile && !existingPayment?.receipt_url) {
      setErrorMsg('Please upload a clear photo or PDF document of your bank deposit slip or Mobile Money confirmation.');
      return;
    }
    if (!transactionRef.trim()) {
      setErrorMsg('Please enter the SMS or bank transaction reference number.');
      return;
    }
    if (!submittedAmount || parseFloat(submittedAmount) <= 0) {
      setErrorMsg('Please enter the exact amount paid.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      let receiptUrl = existingPayment?.receipt_url || '';
      let receiptFileName = receiptFile?.name || existingPayment?.receipt_file_name || '';

      // Upload file to Supabase Storage if provided
      if (receiptFile) {
        const fileExt = receiptFile.name.split('.').pop();
        const fileName = `receipt_${Date.now()}_${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
        const filePath = `${user?.id || 'guest'}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('payment-receipts')
          .upload(filePath, receiptFile, { upsert: true });

        if (uploadError) {
          console.warn('Storage upload error (proceeding with fallback URL):', uploadError.message);
          receiptUrl = `https://storage.bientomart.com/receipts/${fileName}`;
        } else {
          const { data: urlData } = supabase.storage
            .from('payment-receipts')
            .getPublicUrl(filePath);
          receiptUrl = urlData.publicUrl;
        }
        receiptFileName = receiptFile.name;
      }

      const paymentReference =
        existingPayment?.payment_reference ||
        `PAY-${Date.now().toString().slice(-6)}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

      // Insert or update payment record
      if (existingPayment) {
        await supabase
          .from('payments')
          .update({
            receipt_url: receiptUrl,
            receipt_file_name: receiptFileName,
            submitted_amount: parseFloat(submittedAmount),
            currency,
            payment_method: paymentMethod,
            provider_tx_id: transactionRef.trim(),
            payment_date: paymentDate,
            customer_notes: customerNotes,
            status: 'Payment Under Review',
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingPayment.id);
      } else {
        await supabase.from('payments').insert({
          payment_reference: paymentReference,
          invoice_id: invoice?.id || null,
          course_id: course?.id || null,
          client_id: user?.id,
          amount: parseFloat(submittedAmount),
          expected_amount: expectedAmount > 0 ? expectedAmount : parseFloat(submittedAmount),
          submitted_amount: parseFloat(submittedAmount),
          currency,
          provider: paymentMethod.includes('Bank') ? 'Bank Transfer' : 'Mobile Money',
          payment_method: paymentMethod,
          provider_tx_id: transactionRef.trim(),
          payment_date: paymentDate,
          customer_notes: customerNotes,
          receipt_url: receiptUrl,
          receipt_file_name: receiptFileName,
          status: 'Payment Under Review',
          metadata: course ? { course_id: course.id, course_title: course.title } : undefined,
        });
      }

      // Send transactional confirmation email
      if (user?.email) {
        await sendEmail({
          recipient: user.email,
          recipientName: profile?.full_name || 'Client',
          subject: 'Payment Receipt Submitted — Payment Under Review',
          templateType: 'receipt_submitted',
          referenceNumber: paymentReference,
          details: {
            amount: submittedAmount,
            currency,
            method: paymentMethod,
            transactionRef: transactionRef.trim(),
          },
        });
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
        setSuccess(false);
      }, 2500);
    } catch (err: any) {
      console.error('Receipt submission error:', err);
      setErrorMsg(err.message || 'Failed to submit payment receipt.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const currentInstruction = instructions.find((i) => i.id === selectedInstructionId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 sm:p-8 border border-gray-100 max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b border-gray-100 mb-5">
          <div>
            <span className="text-[11px] font-bold text-gray-400 font-mono block">
              {course
                ? `Course Enrollment • ${course.title}`
                : invoice
                ? `Invoice #${invoice.invoice_number}`
                : 'Service Payment'}
            </span>
            <h3 className="text-xl font-bold text-darkBlue-900">
              Manual Payment & Receipt Submission
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {success ? (
          <div className="text-center py-10">
            <div className="w-16 h-16 bg-blue-50 text-clairBlue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-9 h-9" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">Receipt Submitted Successfully!</h4>
            <div className="inline-block px-3 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full text-xs font-bold mb-4">
              Status: PAYMENT UNDER REVIEW
            </div>
            <p className="text-xs text-gray-600 max-w-md mx-auto leading-relaxed">
              Your payment receipt and transaction reference have been safely received. A BientoMart Finance Officer will manually verify the funds with our bank / mobile operator and approve your project/course within 2-4 business hours.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmitReceipt} className="space-y-6 text-xs sm:text-sm">
            {/* Step 1: Official BientoMart Payment Channels */}
            <div>
              <div className="flex items-center gap-2 mb-2 font-bold text-darkBlue-900 uppercase tracking-wider text-xs">
                <span>1. Select Official BientoMart Payment Channel</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                {instructions.map((inst) => {
                  const isSelected = selectedInstructionId === inst.id;
                  const isMomo = inst.channel_type === 'Mobile Money';

                  return (
                    <div
                      key={inst.id}
                      onClick={() => handleInstructionChange(inst.id)}
                      className={`p-3 rounded-2xl border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-clairBlue-500 bg-clairBlue-50/60 shadow-sm'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 font-bold text-darkBlue-900 text-xs mb-1">
                        {isMomo ? (
                          <Smartphone className="w-4 h-4 text-amber-500" />
                        ) : (
                          <Building className="w-4 h-4 text-purple-500" />
                        )}
                        <span>{inst.provider}</span>
                      </div>
                      <div className="text-[11px] text-gray-600 font-mono font-semibold">
                        A/C: {inst.account_number}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        Holder: {inst.account_holder}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Selected Channel Instructions Card */}
              {currentInstruction && (
                <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-200 text-xs space-y-1">
                  <div className="font-bold text-gray-800 text-[11px]">
                    How to pay via {currentInstruction.provider}:
                  </div>
                  <p className="text-gray-600 leading-relaxed text-[11px]">
                    {currentInstruction.instructions}
                  </p>
                </div>
              )}
            </div>

            {/* Step 2: Payment Details */}
            <div>
              <div className="flex items-center gap-2 mb-3 font-bold text-darkBlue-900 uppercase tracking-wider text-xs">
                <span>2. Enter Your External Payment Details</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-gray-700 mb-1">
                    Amount Paid *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    required
                    value={submittedAmount}
                    onChange={(e) => setSubmittedAmount(e.target.value)}
                    placeholder="e.g. 250.00"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold text-darkBlue-900 focus:outline-none focus:ring-2 focus:ring-clairBlue-500"
                  />
                  {expectedAmount > 0 && (
                    <span className="text-[10px] text-gray-400 mt-0.5 block">
                      {course ? `Tuition: $${expectedAmount.toFixed(2)}` : `Balance Due: $${expectedAmount.toFixed(2)}`}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-gray-700 mb-1">
                    Currency *
                  </label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="UGX">UGX (Uganda Shillings)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-gray-700 mb-1">
                    Payment Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs"
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-[11px] font-bold text-gray-700 mb-1">
                  Transaction / Mobile Money / Bank Slip Reference ID *
                </label>
                <input
                  type="text"
                  required
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  placeholder="e.g. MTN SMS Ref: 394829104 or Stanbic Slip # 84920"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono font-semibold"
                />
              </div>

              <div className="mt-3">
                <label className="block text-[11px] font-bold text-gray-700 mb-1">
                  Optional Note / Sender Name
                </label>
                <input
                  type="text"
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  placeholder="e.g. Paid from 0773000000 under John Doe"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs"
                />
              </div>
            </div>

            {/* Step 3: Receipt Upload */}
            <div>
              <div className="flex items-center gap-2 mb-2 font-bold text-darkBlue-900 uppercase tracking-wider text-xs">
                <span>3. Upload Bank Slip / Mobile Money Receipt File *</span>
              </div>

              <div className="border-2 border-dashed border-gray-200 rounded-2xl p-5 text-center bg-gray-50/50 hover:bg-gray-50 transition-colors">
                <input
                  type="file"
                  id="receipt-file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <label htmlFor="receipt-file" className="cursor-pointer block">
                  <Upload className="w-8 h-8 text-clairBlue-500 mx-auto mb-2" />
                  <span className="font-bold text-xs text-darkBlue-900 block">
                    {receiptFile ? receiptFile.name : 'Click to upload proof of payment'}
                  </span>
                  <span className="text-[10px] text-gray-400 block mt-1">
                    Accepts PNG, JPG, or PDF (Max 10 MB)
                  </span>
                </label>
              </div>
            </div>

            {/* Warning Rule: Uploading does NOT automatically approve */}
            <div className="p-3.5 bg-amber-50/80 border border-amber-200 rounded-2xl flex items-start gap-2.5 text-[11px] text-amber-900">
              <ShieldAlert className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Manual Verification Policy:</strong> Invoices and courses are only activated after an authorized BientoMart Finance Officer verifies the received funds. Uploading a receipt places your submission in <strong>Payment Under Review</strong> status.
              </span>
            </div>

            {errorMsg && (
              <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded-xl text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-1.5 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Uploading Proof...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    <span>Submit Proof of Payment</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
